import React, { useState, useEffect, useRef, memo, useMemo, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { 
  ArrowLeft, Play, Pause, Send, Users, MessageCircle, 
  AlertCircle, Loader2, Crown, Trophy, User, X,
  Smile, Heart, ChevronUp, ChevronDown, Radio, Trash2, Bell, AtSign,
  Volume2, VolumeX, Maximize, Minimize, Rewind, SkipBack, BarChart2, 
  Users2, Share2
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Stream } from '@cloudflare/stream-react';
import Hls from 'hls.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Common emojis for quick access
const QUICK_EMOJIS = ['❤️', '🔥', '👏', '😂', '🎉', '💯', '🙌', '😍', '👀', '💪', '🎵', '🎤'];

// Mobile Video Player with direct audio control and HLS.js support
const MobileVideoPlayer = ({ streamUrl, viewerIdentifier, scorecard, event, videoRef, isMuted, viewerCount, containerRef }) => {
  const isCloudflareId = streamUrl && /^[a-f0-9]{32}$/i.test(streamUrl);
  const hlsRef = useRef(null);
  
  // Initialize HLS.js for non-Safari browsers (Chrome, Android)
  React.useEffect(() => {
    if (!videoRef?.current || !isCloudflareId) return;
    
    // Check if session has been terminated
    if (window._sessionTerminated) {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.muted = true;
      }
      return;
    }
    
    const video = videoRef.current;
    const hlsUrl = `https://customer-xg4xjbetrvqn62vp.cloudflarestream.com/${streamUrl}/manifest/video.m3u8`;
    
    // Check if native HLS is supported (Safari, iOS)
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsUrl;
      video.play().catch(() => {});
    } else if (Hls.isSupported()) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
      });
      
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        // Double-check session before playing
        if (!window._sessionTerminated) {
          video.play().catch(() => {});
        }
      });
      
      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.error('HLS fatal error:', data.type, data.details);
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad();
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
          }
        }
      });
      
      hlsRef.current = hls;
    } else {
      video.src = hlsUrl;
    }
    
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [streamUrl, isCloudflareId, videoRef]);
  
  // Sync muted state to video element
  React.useEffect(() => {
    if (videoRef?.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted, videoRef]);

  // Scorecard Overlay
  const ScorecardOverlay = () => {
    if (!scorecard || !scorecard.is_visible) return null;
    
    if (scorecard.mode === 'open' && scorecard.contestants?.length > 0) {
      return (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-30 pointer-events-none">
          <div className="bg-[#0a0a0a]/80 backdrop-blur-xl rounded-2xl px-4 py-3 border border-white/20 shadow-2xl">
            {scorecard.round_info && (
              <p className="text-center text-xs text-yellow-400 font-bold mb-2">{scorecard.round_info}</p>
            )}
            <div className="flex items-center gap-4">
              {scorecard.contestants.slice(0, 6).map((contestant, idx) => (
                <div key={idx} className="text-center min-w-[60px]">
                  <p className="text-white font-medium text-xs truncate max-w-[70px]">{contestant.name}</p>
                  <p className="text-2xl font-bold text-yellow-400">{contestant.score}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }
    
    if (scorecard.mode === 'battle' && (scorecard.battle_left || scorecard.battle_right)) {
      return (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-30 pointer-events-none">
          <div className="bg-[#0a0a0a]/80 backdrop-blur-xl rounded-2xl px-6 py-3 border border-white/20 shadow-2xl">
            {scorecard.round_info && (
              <p className="text-center text-xs text-gray-400 mb-2">{scorecard.round_info}</p>
            )}
            <div className="flex items-center gap-6">
              <div className="text-center min-w-[80px]">
                <p className="text-white font-bold text-sm truncate max-w-[100px]">
                  {scorecard.battle_left?.name || '—'}
                </p>
                <p className="text-4xl font-bold text-red-400">{scorecard.battle_left?.score || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-white/50 text-2xl font-bold">-</p>
              </div>
              <div className="text-center min-w-[80px]">
                <p className="text-white font-bold text-sm truncate max-w-[100px]">
                  {scorecard.battle_right?.name || '—'}
                </p>
                <p className="text-4xl font-bold text-blue-400">{scorecard.battle_right?.score || 0}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return null;
  };
  
  return (
    <>
      {streamUrl ? (
        <div ref={containerRef} className="absolute inset-0 bg-black">
          {isCloudflareId ? (
            <video
              ref={videoRef}
              className="w-full h-full object-contain bg-black"
              autoPlay
              playsInline
              muted={isMuted}
              controls={false}
              controlsList="nodownload noplaybackrate"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
              webkit-playsinline="true"
              x5-playsinline="true"
              data-testid="cloudflare-mobile-player"
            />
          ) : streamUrl.includes('youtube.com') || streamUrl.includes('youtu.be') ? (
            <iframe
              src={streamUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/') + '?autoplay=1&controls=1'}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video 
              ref={videoRef} 
              src={streamUrl} 
              className="w-full h-full object-contain bg-black"
              autoPlay 
              playsInline
              muted={isMuted}
              controls={false}
              controlsList="nodownload noplaybackrate"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
              webkit-playsinline="true"
            />
          )}
          
          {/* LIVE Badge & Viewer Count - Top Right (Mobile) */}
          {event?.status === 'live' && (
            <div className="absolute top-4 right-4 z-30 pointer-events-none">
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-1.5 bg-red-500 rounded px-2.5 py-1">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  <span className="text-white text-sm font-bold">LIVE</span>
                </div>
                {viewerCount > 0 && (
                  <div className="flex items-center gap-1.5 bg-black/60 backdrop-blur-sm rounded px-2 py-1">
                    <Users className="h-3.5 w-3.5 text-white" />
                    <span className="text-white text-xs font-medium">{viewerCount}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <ScorecardOverlay />
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
          <ScorecardOverlay />
          <div className="text-center p-8">
            <div className="w-24 h-24 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6">
              <Play className="h-12 w-12 text-white/30" />
            </div>
            {event?.status === 'upcoming' ? (
              <>
                <h3 className="text-2xl font-bold text-white mb-2">Stream Starting Soon</h3>
                <p className="text-gray-400">The live stream will begin at the scheduled time</p>
              </>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-white mb-2">Stream Not Available</h3>
                <p className="text-gray-400">Please check back later</p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// Memoized Video Player Component - Clean without watermarks
const MemoizedVideoPlayer = memo(({ iframeUrl, streamUrl, viewerIdentifier, scorecard, event, containerRef, viewerCount = 0 }) => {
  const localContainerRef = useRef(null);
  const effectiveContainerRef = containerRef || localContainerRef;
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const isCloudflareId = streamUrl && /^[a-f0-9]{32}$/i.test(streamUrl);
  const isLiveInputId = isCloudflareId;  // Same check for both
  
  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);
  
  // Build the Cloudflare iframe URL
  const getCloudflareIframeUrl = () => {
    if (iframeUrl) {
      const hasOptions = iframeUrl.includes('?');
      return iframeUrl + (hasOptions ? '&' : '?') + 'autoplay=true&muted=true&preload=auto&controls=true&primaryColor=ef4444';
    }
    
    const defaultCustomerCode = 'xg4xjbetrvqn62vp';
    if (streamUrl) {
      return `https://customer-${defaultCustomerCode}.cloudflarestream.com/${streamUrl}/iframe?autoplay=true&muted=true&preload=auto&controls=true&primaryColor=ef4444`;
    }
    
    return null;
  };
  
  // Toggle fullscreen
  const toggleFullscreen = () => {
    const container = effectiveContainerRef.current;
    if (!container) return;
    
    const isInFullscreen = document.fullscreenElement || document.webkitFullscreenElement;
    
    if (isInFullscreen) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    } else {
      if (container.requestFullscreen) container.requestFullscreen();
      else if (container.webkitRequestFullscreen) container.webkitRequestFullscreen();
    }
  };

  // Scorecard Overlay Component
  const ScorecardOverlay = () => {
    if (!scorecard || !scorecard.is_visible) return null;
    
    if (scorecard.mode === 'open' && scorecard.contestants?.length > 0) {
      return (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-30 pointer-events-none">
          <div className="bg-[#0a0a0a]/80 backdrop-blur-xl rounded-2xl px-4 py-3 border border-white/20 shadow-2xl">
            {scorecard.round_info && (
              <p className="text-center text-xs text-yellow-400 font-bold mb-2">{scorecard.round_info}</p>
            )}
            <div className="flex items-center gap-4">
              {scorecard.contestants.slice(0, 6).map((contestant, idx) => (
                <div key={idx} className="text-center min-w-[60px]">
                  <p className="text-white font-medium text-xs truncate max-w-[70px]">{contestant.name}</p>
                  <p className="text-2xl font-bold text-yellow-400">{contestant.score}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }
    
    if (scorecard.mode === 'battle' && (scorecard.battle_left || scorecard.battle_right)) {
      return (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-30 pointer-events-none">
          <div className="bg-[#0a0a0a]/80 backdrop-blur-xl rounded-2xl px-6 py-3 border border-white/20 shadow-2xl">
            {scorecard.round_info && (
              <p className="text-center text-xs text-gray-400 mb-2">{scorecard.round_info}</p>
            )}
            <div className="flex items-center gap-6">
              <div className="text-center min-w-[80px]">
                <p className="text-white font-bold text-sm truncate max-w-[100px]">
                  {scorecard.battle_left?.name || '—'}
                </p>
                <p className="text-4xl font-bold text-red-400">{scorecard.battle_left?.score || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-white/50 text-2xl font-bold">-</p>
              </div>
              <div className="text-center min-w-[80px]">
                <p className="text-white font-bold text-sm truncate max-w-[100px]">
                  {scorecard.battle_right?.name || '—'}
                </p>
                <p className="text-4xl font-bold text-blue-400">{scorecard.battle_right?.score || 0}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return null;
  };
  
  return (
    <>
      {streamUrl ? (
        <div ref={effectiveContainerRef} className="absolute inset-0 bg-black">
          {isLiveInputId || isCloudflareId ? (
            <iframe
              src={getCloudflareIframeUrl()}
              className="w-full h-full border-0"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              title="Live Stream"
              data-testid="cloudflare-stream-player"
            />
          ) : streamUrl.includes('cloudflarestream.com') || streamUrl.includes('videodelivery.net') ? (
            <iframe
              src={streamUrl}
              className="w-full h-full border-0"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              title="Stream"
            />
          ) : streamUrl.includes('youtube.com') || streamUrl.includes('youtu.be') ? (
            <iframe
              src={streamUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/') + '?autoplay=1&controls=1'}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : streamUrl.includes('vimeo.com') ? (
            <iframe
              src={streamUrl.replace('vimeo.com/', 'player.vimeo.com/video/')}
              className="w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video 
              src={streamUrl} 
              autoPlay 
              playsInline
              controls={false}
              controlsList="nodownload noplaybackrate"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
              className="w-full h-full object-contain" 
            />
          )}
          
          {/* LIVE Badge & Viewer Count - Top Right */}
          {event?.status === 'live' && (
            <div className="absolute top-4 right-4 z-30 pointer-events-none">
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-1.5 bg-red-500 rounded px-2.5 py-1">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  <span className="text-white text-sm font-bold">LIVE</span>
                </div>
                {viewerCount > 0 && (
                  <div className="flex items-center gap-1.5 bg-black/60 backdrop-blur-sm rounded px-2 py-1">
                    <Users className="h-3.5 w-3.5 text-white" />
                    <span className="text-white text-xs font-medium">{viewerCount}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <ScorecardOverlay />
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
          <ScorecardOverlay />
          <div className="text-center p-8">
            <div className="w-24 h-24 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6">
              <Play className="h-12 w-12 text-white/30" />
            </div>
            {event?.status === 'upcoming' ? (
              <>
                <h3 className="text-2xl font-bold text-white mb-2">Stream Starting Soon</h3>
                <p className="text-gray-400">The live stream will begin at the scheduled time</p>
              </>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-white mb-2">Stream Not Available</h3>
                <p className="text-gray-400">Please check back later</p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these specific props change
  // IMPORTANT: We do NOT compare iframeUrl changes to prevent player reload
  return prevProps.streamUrl === nextProps.streamUrl &&
         prevProps.viewerIdentifier === nextProps.viewerIdentifier &&
         JSON.stringify(prevProps.scorecard) === JSON.stringify(nextProps.scorecard);
});

// Helper to render message with highlighted mentions
const renderMessageWithMentions = (message, currentUsername) => {
  const parts = message.split(/(@\w+)/g);
  return parts.map((part, index) => {
    if (part.startsWith('@')) {
      const username = part.slice(1);
      const isCurrentUser = username.toLowerCase() === currentUsername?.toLowerCase();
      return (
        <span 
          key={index} 
          className={`font-bold ${isCurrentUser ? 'text-yellow-400 bg-yellow-400/20 px-1 rounded' : 'text-purple-400'}`}
        >
          {part}
        </span>
      );
    }
    return part;
  });
};

// Memoized Chat Message Component - prevents re-renders when parent updates
const ChatMessage = memo(({ msg, isMobile, formatTime, currentUsername, isModerator, onDelete }) => (
  <div className="flex items-start gap-2 group">
    {msg.profile_picture ? (
      <img 
        src={msg.profile_picture} 
        alt=""
        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
      />
    ) : (
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
        <User className="h-4 w-4 text-white" />
      </div>
    )}
    
    <div className={`${isMobile ? 'bg-white/10 backdrop-blur-sm rounded-2xl rounded-tl-sm px-3 py-2 max-w-[80%]' : 'flex-1'}`}>
      <div className="flex items-baseline gap-2">
        <span className="font-bold text-sm text-purple-400">{msg.username}</span>
        <span className="text-xs text-gray-500">{formatTime(msg.timestamp)}</span>
        {isModerator && onDelete && (
          <button 
            onClick={() => onDelete(msg.id)}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-300 ml-auto"
            title="Delete message"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </div>
      <p className="text-white text-sm break-words">
        {renderMessageWithMentions(msg.message, currentUsername)}
      </p>
    </div>
  </div>
));

const LiveWatchStream = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const sessionCheckRef = useRef(null);
  const inputRef = useRef(null);
  const desktopInputRef = useRef(null);
  
  const [streamData, setStreamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionError, setSessionError] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showChat, setShowChat] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);
  
  // Voting state
  const [voteResults, setVoteResults] = useState(null);
  const [selectedVote, setSelectedVote] = useState('');
  const [voting, setVoting] = useState(false);
  const [totalVotes, setTotalVotes] = useState(0);
  const [showVoting, setShowVoting] = useState(false);
  
  // Winner announcement state
  const [winnerAnnounced, setWinnerAnnounced] = useState(false);
  const [winner, setWinner] = useState(null);
  const [resultsVisible, setResultsVisible] = useState(false);
  const [showWinnerOverlay, setShowWinnerOverlay] = useState(false);

  // Scorecard state
  const [scorecard, setScorecard] = useState(null);

  // Viewer count state
  const [viewerCount, setViewerCount] = useState(0);

  // Notifications & moderation state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isModerator, setIsModerator] = useState(false);

  // Username modal state
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [savingUsername, setSavingUsername] = useState(false);
  const [usernameError, setUsernameError] = useState('');

  // Voting modal state
  const [showVotingModal, setShowVotingModal] = useState(false);

  // Video control states (for mobile)
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMuted, setIsMuted] = useState(true);  // Start muted, user taps to unmute
  const [isPlaying, setIsPlaying] = useState(true);
  const [isPiPActive, setIsPiPActive] = useState(false);
  
  // Video ref for direct control
  const mobileVideoRef = useRef(null);
  const videoContainerRef = useRef(null);  // Container ref for fullscreen

  // Signed URL state
  const [signedUrl, setSignedUrl] = useState(null);
  const [signedUrlExpiry, setSignedUrlExpiry] = useState(null);
  const [playerIframeUrl, setPlayerIframeUrl] = useState(null);  // Cloudflare iframe URL
  const [customerCode, setCustomerCode] = useState(null);  // Cloudflare customer code

  // Deletion tracking for real-time sync
  const lastDeletionCheckRef = useRef(null);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent keyboard from pushing up the page on iOS (TikTok style)
  useEffect(() => {
    if (isMobile) {
      // Store original viewport
      const viewport = document.querySelector('meta[name=viewport]');
      const originalContent = viewport?.getAttribute('content');
      
      // Set viewport to prevent resizing
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, interactive-widget=resizes-content');
      }
      
      // Prevent scroll when keyboard opens
      const handleFocus = () => {
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100%';
      };
      
      const handleBlur = () => {
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.height = '';
      };
      
      document.addEventListener('focusin', handleFocus);
      document.addEventListener('focusout', handleBlur);
      
      return () => {
        // Restore original viewport
        if (viewport && originalContent) {
          viewport.setAttribute('content', originalContent);
        }
        document.removeEventListener('focusin', handleFocus);
        document.removeEventListener('focusout', handleBlur);
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.height = '';
      };
    }
  }, [isMobile]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Picture-in-Picture (PiP) - Auto enable when user leaves the page/app
  useEffect(() => {
    const video = mobileVideoRef.current;
    if (!video) return;
    
    // Check if PiP is supported (standard or webkit)
    const isPiPSupported = document.pictureInPictureEnabled || 
                          (video.webkitSupportsPresentationMode && 
                           typeof video.webkitSetPresentationMode === 'function');
    
    if (!isPiPSupported) return;
    
    // Handle page visibility change (user switches app/tab)
    const handleVisibilityChange = async () => {
      if (document.hidden && video && !video.paused) {
        try {
          // Try standard PiP first
          if (document.pictureInPictureEnabled && !document.pictureInPictureElement) {
            await video.requestPictureInPicture();
            setIsPiPActive(true);
          } 
          // iOS Safari uses webkitSetPresentationMode
          else if (video.webkitSupportsPresentationMode && 
                   video.webkitPresentationMode !== 'picture-in-picture') {
            video.webkitSetPresentationMode('picture-in-picture');
            setIsPiPActive(true);
          }
        } catch (err) {
          console.log('PiP auto-start not available:', err.message);
        }
      }
    };
    
    // Track PiP state changes
    const handlePiPEnter = () => setIsPiPActive(true);
    const handlePiPLeave = () => setIsPiPActive(false);
    
    // iOS Safari presentation mode change
    const handleWebkitPresentationChange = () => {
      setIsPiPActive(video.webkitPresentationMode === 'picture-in-picture');
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    video.addEventListener('enterpictureinpicture', handlePiPEnter);
    video.addEventListener('leavepictureinpicture', handlePiPLeave);
    video.addEventListener('webkitpresentationmodechanged', handleWebkitPresentationChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      video.removeEventListener('enterpictureinpicture', handlePiPEnter);
      video.removeEventListener('leavepictureinpicture', handlePiPLeave);
      video.removeEventListener('webkitpresentationmodechanged', handleWebkitPresentationChange);
    };
  }, [mobileVideoRef.current]);

  // Toggle Picture-in-Picture manually - simple version without watermarks
  const togglePiP = async () => {
    const video = mobileVideoRef.current;
    
    if (!video) {
      console.log('No video element for PiP');
      return;
    }
    
    try {
      // Make sure video is playing first
      if (video.paused) {
        try {
          await video.play();
        } catch (playErr) {
          console.log('Could not start video for PiP:', playErr.message);
        }
      }
      
      // Check video readyState - must have loaded metadata
      if (video.readyState < 1) {
        console.log('Video not ready for PiP - stream may not be active');
        return;
      }
      
      // Check if currently in PiP mode
      const isCurrentlyPiP = document.pictureInPictureElement || 
                             video.webkitPresentationMode === 'picture-in-picture';
      
      if (isCurrentlyPiP) {
        // Exit PiP
        if (document.pictureInPictureElement) {
          await document.exitPictureInPicture();
        } else if (video.webkitSetPresentationMode) {
          video.webkitSetPresentationMode('inline');
        }
        setIsPiPActive(false);
      } else {
        // Enter PiP
        if (document.pictureInPictureEnabled) {
          await video.requestPictureInPicture();
          setIsPiPActive(true);
        } else if (video.webkitSupportsPresentationMode && 
                   typeof video.webkitSetPresentationMode === 'function') {
          // iOS Safari
          video.webkitSetPresentationMode('picture-in-picture');
          setIsPiPActive(true);
        } else {
          console.log('PiP not supported on this browser');
        }
      }
    } catch (err) {
      console.error('PiP error:', err.message);
    }
  };

  // Listen for player state messages from Cloudflare iframe - simplified to avoid re-renders
  useEffect(() => {
    const handlePlayerMessage = (event) => {
      // Only process messages from Cloudflare domains
      if (!event.origin.includes('cloudflarestream.com') && !event.origin.includes('videodelivery.net')) {
        return;
      }
      // We no longer sync state from iframe messages to avoid re-render issues
      // User controls mute/play via our custom buttons
    };
    
    window.addEventListener('message', handlePlayerMessage);
    return () => window.removeEventListener('message', handlePlayerMessage);
  }, []);

  // Auto-unmute audio when stream loads - DISABLED (user must tap unmute button)
  // Browser policies require user interaction for audio

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('livestreamToken')}` }
  });

  useEffect(() => {
    const token = localStorage.getItem('livestreamToken');
    const customerData = localStorage.getItem('livestreamCustomer');
    
    if (!token) {
      localStorage.setItem('intendedEvent', slug);
      navigate('/live/login');
      return;
    }

    if (customerData) {
      try {
        const parsed = JSON.parse(customerData);
        setCustomer(parsed);
        setIsModerator(parsed.is_moderator || false);
      } catch (e) {
        console.error('Error parsing customer data');
      }
    }

    fetchStreamData();
    fetchProfile(); // Fetch latest profile to get moderator status

    // Session check every 30 seconds
    sessionCheckRef.current = setInterval(() => {
      verifySession();
    }, 30000);

    return () => {
      if (sessionCheckRef.current) clearInterval(sessionCheckRef.current);
    };
  }, [slug, navigate]);

  // Fetch profile to get latest moderator status and username
  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/customer/profile`, getAuthHeaders());
      setIsModerator(res.data.is_moderator || false);
      
      // Update local storage and customer state with latest data
      const customerData = JSON.parse(localStorage.getItem('livestreamCustomer') || '{}');
      customerData.is_moderator = res.data.is_moderator;
      if (res.data.username) {
        customerData.username = res.data.username;
      }
      localStorage.setItem('livestreamCustomer', JSON.stringify(customerData));
      
      // Also update customer state
      setCustomer(prev => ({
        ...prev,
        ...customerData,
        username: res.data.username || prev?.username || customerData.username
      }));
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  // Ref to track if initial iframe URL has been set (prevents reload on token refresh)
  const initialIframeUrlSetRef = useRef(false);

  // Fetch signed URL for secure playback
  const fetchSignedUrl = async (forceUpdate = false) => {
    if (!streamData?.event?.id || !customer?.id) return;
    
    try {
      const res = await axios.post(`${API}/cloudflare/signed-url`, {
        event_id: streamData.event.id,
        customer_id: customer.id
      }, getAuthHeaders());
      
      // Store customer code for building iframe URLs
      if (res.data.customer_code && !customerCode) {
        setCustomerCode(res.data.customer_code);
      }
      
      // IMPORTANT: Only set iframe URL on FIRST load to prevent player reload
      // Subsequent refreshes only update the signed token for API calls, not the player
      if (!initialIframeUrlSetRef.current || forceUpdate) {
        if (res.data.signed_iframe_url) {
          setPlayerIframeUrl(res.data.signed_iframe_url);
          initialIframeUrlSetRef.current = true;
        } else if (res.data.unsigned_iframe_url) {
          setPlayerIframeUrl(res.data.unsigned_iframe_url);
          initialIframeUrlSetRef.current = true;
        }
      }
      
      // Always update the token and expiry (for background refresh)
      if (res.data.signed_token) {
        setSignedUrl(res.data.signed_token);
        setSignedUrlExpiry(new Date(res.data.expires_at));
      }
    } catch (err) {
      console.error('Error fetching signed URL:', err);
    }
  };

  // Refresh signed URL before it expires (silently - doesn't reload player)
  useEffect(() => {
    if (!signedUrlExpiry) return;
    
    // Refresh 5 minutes before expiry
    const refreshTime = new Date(signedUrlExpiry.getTime() - 5 * 60 * 1000);
    const now = new Date();
    const timeUntilRefresh = refreshTime.getTime() - now.getTime();
    
    if (timeUntilRefresh > 0) {
      const timeout = setTimeout(() => {
        fetchSignedUrl(false); // Don't force update - just refresh token
      }, timeUntilRefresh);
      
      return () => clearTimeout(timeout);
    }
  }, [signedUrlExpiry]);

  // Fetch signed URL when stream data is loaded
  useEffect(() => {
    if (streamData?.event?.id && customer?.id) {
      fetchSignedUrl(true); // Force update on initial load
    }
  }, [streamData?.event?.id, customer?.id]);

  // Chat polling
  useEffect(() => {
    if (!streamData?.event?.id) return;
    
    fetchMessages();
    
    // Poll for new messages
    const chatInterval = setInterval(() => {
      fetchNewMessages();
    }, 2000);
    
    // Poll for deleted messages (real-time moderation sync)
    const deletionInterval = setInterval(() => {
      checkDeletedMessages();
    }, 2000);

    return () => {
      clearInterval(chatInterval);
      clearInterval(deletionInterval);
    };
  }, [streamData?.event?.id]);

  // Notification polling - check for mentions every 10 seconds
  useEffect(() => {
    if (!streamData?.event?.id) return;
    
    fetchNotifications();
    const notifInterval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(notifInterval);
  }, [streamData?.event?.id]);

  // Voting polling - DISABLED for blind voting (results only shown after winner announcement)
  // The voting status polling handles winner announcements separately
  // useEffect(() => {
  //   if (!streamData?.voting_enabled || !streamData?.event?.slug) return;
  //   
  //   fetchVoteResults();
  //   const voteInterval = setInterval(fetchVoteResults, 5000);
  //   return () => clearInterval(voteInterval);
  // }, [streamData?.voting_enabled, streamData?.event?.slug]);

  // Scorecard polling - every 5 seconds
  useEffect(() => {
    if (!streamData?.event?.id) return;
    
    fetchScorecard();
    const scorecardInterval = setInterval(fetchScorecard, 5000);
    return () => clearInterval(scorecardInterval);
  }, [streamData?.event?.id]);

  // Voting status polling - check for winner announcement every 5 seconds (reduced frequency)
  // PAUSE polling when modal is open to prevent flashing
  useEffect(() => {
    if (!streamData?.event?.id || !streamData?.voting_enabled) return;
    if (showVotingModal) return; // Don't poll while modal is open - prevents flashing
    
    const fetchVotingStatus = async () => {
      try {
        const response = await axios.get(
          `${API}/customer/voting/${streamData.event.id}/status`,
          getAuthHeaders()
        );
        
        const questions = response.data.questions || [];
        if (questions.length > 0) {
          const q = questions[0]; // Get first active question
          
          // Only update state if there's an actual change to prevent re-renders
          if (q.winner_announced && !winnerAnnounced) {
            // Winner was just announced
            setWinnerAnnounced(true);
            setWinner(q.winner);
            setResultsVisible(q.results_visible);
            setShowWinnerOverlay(true);
            
            if (q.results_visible && q.vote_counts) {
              const results = Object.entries(q.vote_counts).map(([option, votes]) => ({
                option,
                votes
              }));
              setVoteResults(results);
              setTotalVotes(q.total_votes || 0);
            }
          } else if (!q.winner_announced && winnerAnnounced) {
            // Winner was hidden, reset state
            setWinnerAnnounced(false);
            setWinner(null);
            setResultsVisible(false);
            setShowWinnerOverlay(false);
          }
          // Don't update state if nothing changed - this prevents jumping
        }
      } catch (err) {
        // Silently fail - don't spam console
      }
    };

    // Initial fetch after a delay to prevent initial jumping
    const initialTimeout = setTimeout(fetchVotingStatus, 2000);
    // Poll less frequently (every 5 seconds instead of 3)
    const votingStatusInterval = setInterval(fetchVotingStatus, 5000);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(votingStatusInterval);
    };
  }, [streamData?.event?.id, streamData?.voting_enabled, winnerAnnounced, showVotingModal]);

  // Viewer heartbeat (for live tracking)
  useEffect(() => {
    if (!streamData?.event?.id) return;
    
    const sendHeartbeat = async () => {
      try {
        // Get location (simplified - in production you'd use a geo-IP service)
        const res = await axios.post(`${API}/livestream/viewer/heartbeat`, {
          event_id: streamData.event.id,
          country: 'UK',  // In production, get from IP geolocation
          city: 'London'
        }, getAuthHeaders());
        
        // Update viewer count from response
        if (res.data.viewer_count !== undefined) {
          setViewerCount(res.data.viewer_count);
        }
      } catch (err) {
        console.error('Heartbeat failed:', err);
      }
    };
    
    sendHeartbeat();
    const heartbeatInterval = setInterval(sendHeartbeat, 30000);
    return () => clearInterval(heartbeatInterval);
  }, [streamData?.event?.id]);

  // Auto-scroll chat - only when new messages are added
  const prevMessagesLength = useRef(0);
  const isUserScrolling = useRef(false);
  
  useEffect(() => {
    // Only auto-scroll if:
    // 1. There are new messages (length increased)
    // 2. User is not manually scrolling up
    if (messages.length > prevMessagesLength.current && !isUserScrolling.current) {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }
    prevMessagesLength.current = messages.length;
  }, [messages.length]);

  // Track if user is scrolling up in chat
  const handleChatScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      // If user scrolled up more than 100px from bottom, don't auto-scroll
      isUserScrolling.current = scrollHeight - scrollTop - clientHeight > 100;
    }
  };

  const fetchStreamData = async () => {
    try {
      const response = await axios.get(`${API}/livestream/watch/${slug}`, getAuthHeaders());
      setStreamData(response.data);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const verifySession = async () => {
    try {
      await axios.get(`${API}/livestream/auth/me`, getAuthHeaders());
    } catch (err) {
      if (err.response?.status === 401) {
        handleSessionExpired();
      }
    }
  };

  const handleError = (err) => {
    if (err.response?.status === 401) {
      handleSessionExpired();
    } else if (err.response?.status === 403) {
      setError('You don\'t have a ticket for this event');
    } else {
      setError('Failed to load stream');
    }
  };

  const handleSessionExpired = () => {
    setSessionError(true);
    localStorage.removeItem('livestreamToken');
    localStorage.removeItem('livestreamCustomer');
    if (sessionCheckRef.current) clearInterval(sessionCheckRef.current);
  };

  // Use a Set to track message IDs and prevent duplicates
  const messageIdsRef = useRef(new Set());
  const lastFetchTimeRef = useRef(null);

  const fetchMessages = async () => {
    if (!streamData?.event?.id) return;
    try {
      const response = await axios.get(
        `${API}/customer/chat/${streamData.event.id}`,
        getAuthHeaders()
      );
      const fetchedMessages = response.data.messages || [];
      
      // Reset the message IDs set and populate with fetched messages
      messageIdsRef.current = new Set(fetchedMessages.map(m => m.id));
      lastFetchTimeRef.current = fetchedMessages.length > 0 
        ? fetchedMessages[fetchedMessages.length - 1]?.timestamp 
        : new Date().toISOString();
      
      // Initialize deletion tracking timestamp
      lastDeletionCheckRef.current = new Date().toISOString();
      
      setMessages(fetchedMessages);
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };

  // Check for deleted messages (real-time sync)
  const checkDeletedMessages = async () => {
    if (!streamData?.event?.id || !lastDeletionCheckRef.current) return;
    
    try {
      const response = await axios.get(
        `${API}/customer/chat/${streamData.event.id}/deleted?after=${lastDeletionCheckRef.current}`,
        getAuthHeaders()
      );
      
      const { deleted_ids, clear_all, latest_timestamp } = response.data;
      
      if (clear_all) {
        // All messages were cleared - remove everything
        setMessages([]);
        messageIdsRef.current.clear();
      } else if (deleted_ids && deleted_ids.length > 0) {
        // Remove specific deleted messages
        setMessages(prev => prev.filter(m => !deleted_ids.includes(m.id)));
        // Also remove from tracking set
        deleted_ids.forEach(id => messageIdsRef.current.delete(id));
      }
      
      // Update the timestamp for next check
      if (latest_timestamp) {
        lastDeletionCheckRef.current = latest_timestamp;
      }
    } catch (err) {
      console.error('Error checking deleted messages:', err);
    }
  };

  const fetchNewMessages = async () => {
    if (!streamData?.event?.id || !lastFetchTimeRef.current) return;
    
    try {
      const response = await axios.get(
        `${API}/customer/chat/${streamData.event.id}/new?after=${lastFetchTimeRef.current}`,
        getAuthHeaders()
      );
      const newMessages = response.data.messages || [];
      
      if (newMessages.length > 0) {
        // Filter out any messages we already have (by ID)
        const uniqueNewMessages = newMessages.filter(m => !messageIdsRef.current.has(m.id));
        
        if (uniqueNewMessages.length > 0) {
          // Add new message IDs to our set BEFORE updating state
          uniqueNewMessages.forEach(m => messageIdsRef.current.add(m.id));
          
          // Update last fetch time
          lastFetchTimeRef.current = uniqueNewMessages[uniqueNewMessages.length - 1]?.timestamp;
          
          // Append only unique new messages with extra safety check
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const trulyNew = uniqueNewMessages.filter(m => !existingIds.has(m.id));
            if (trulyNew.length === 0) return prev;
            return [...prev, ...trulyNew];
          });
        }
      }
    } catch (err) {
      console.error('Error fetching new messages:', err);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || sendingMessage) return;
    
    // Check customer data - first from localStorage, then from server if needed
    let currentCustomer = JSON.parse(localStorage.getItem('livestreamCustomer') || '{}');
    
    if (!currentCustomer.username) {
      // Try to fetch fresh data from server
      try {
        const profileRes = await axios.get(`${API}/customer/profile`, getAuthHeaders());
        if (profileRes.data.username) {
          currentCustomer = profileRes.data;
          localStorage.setItem('livestreamCustomer', JSON.stringify(currentCustomer));
          setCustomer(currentCustomer);
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
      }
    }
    
    if (!currentCustomer.username) {
      // Open the inline username modal instead of redirecting
      setShowUsernameModal(true);
      return;
    }

    // Store the message text before clearing
    const messageText = newMessage.trim();
    
    // Clear input immediately for better UX
    setNewMessage('');
    setShowEmojiPicker(false);
    setSendingMessage(true);
    
    try {
      const response = await axios.post(
        `${API}/customer/chat/${streamData.event.id}`,
        { message: messageText },
        getAuthHeaders()
      );
      const newMsg = response.data.message;
      
      // Add to our tracking set BEFORE updating state to prevent race condition
      messageIdsRef.current.add(newMsg.id);
      lastFetchTimeRef.current = newMsg.timestamp;
      
      // Use functional update and check for duplicates in state as extra safety
      setMessages(prev => {
        // Double-check that this message isn't already in the array
        if (prev.some(m => m.id === newMsg.id)) {
          return prev; // Already exists, don't add
        }
        return [...prev, newMsg];
      });
    } catch (err) {
      // Restore the message if sending failed
      setNewMessage(messageText);
      alert(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Save username from inline modal
  const saveUsername = async () => {
    if (!newUsername.trim()) {
      setUsernameError('Please enter a username');
      return;
    }

    // Validate username (alphanumeric, 3-20 chars)
    if (!newUsername.match(/^[a-zA-Z0-9]{3,20}$/)) {
      setUsernameError('Username must be 3-20 alphanumeric characters');
      return;
    }

    setSavingUsername(true);
    setUsernameError('');

    try {
      await axios.put(`${API}/customer/profile`, 
        { username: newUsername.trim() },
        getAuthHeaders()
      );

      // Update local state and storage
      const updatedCustomer = { ...customer, username: newUsername.trim() };
      setCustomer(updatedCustomer);
      localStorage.setItem('livestreamCustomer', JSON.stringify(updatedCustomer));
      
      // Close modal and reset
      setShowUsernameModal(false);
      setNewUsername('');
    } catch (err) {
      setUsernameError(err.response?.data?.detail || 'Failed to save username');
    } finally {
      setSavingUsername(false);
    }
  };

  const addEmoji = (emoji) => {
    // Send emoji instantly on mobile
    if (isMobile) {
      sendInstantMessage(emoji);
      setShowEmojiPicker(false);
    } else {
      setNewMessage(prev => prev + emoji);
      desktopInputRef.current?.focus();
    }
  };

  // Send a message instantly (for emojis and reactions)
  const sendInstantMessage = async (messageText) => {
    if (!messageText.trim() || sendingMessage) return;
    
    // Check customer data
    let currentCustomer = JSON.parse(localStorage.getItem('livestreamCustomer') || '{}');
    
    if (!currentCustomer.username) {
      setShowUsernameModal(true);
      return;
    }
    
    setSendingMessage(true);
    
    try {
      const response = await axios.post(
        `${API}/customer/chat/${streamData.event.id}`,
        { message: messageText.trim() },
        getAuthHeaders()
      );
      const newMsg = response.data.message;
      
      messageIdsRef.current.add(newMsg.id);
      lastFetchTimeRef.current = newMsg.timestamp;
      
      setMessages(prev => {
        if (prev.some(m => m.id === newMsg.id)) {
          return prev;
        }
        return [...prev, newMsg];
      });
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle @ mention insertion
  const mentionUser = (username) => {
    setNewMessage(prev => prev + (prev.endsWith(' ') || prev === '' ? '' : ' ') + `@${username} `);
    if (isMobile) {
      inputRef.current?.focus();
    } else {
      desktopInputRef.current?.focus();
    }
  };

  // Fetch notifications (mentions)
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/customer/notifications`, getAuthHeaders());
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  // Mark notifications as read
  const markNotificationsRead = async () => {
    try {
      await axios.post(`${API}/customer/notifications/mark-read`, {}, getAuthHeaders());
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.error('Error marking notifications read:', err);
    }
  };

  // Delete a chat message (moderator only)
  const deleteMessage = async (messageId) => {
    if (!isModerator) return;
    
    try {
      await axios.delete(`${API}/customer/chat/message/${messageId}`, getAuthHeaders());
      // Remove from local state
      setMessages(prev => prev.filter(m => m.id !== messageId));
      messageIdsRef.current.delete(messageId);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete message');
    }
  };

  const fetchVoteResults = async () => {
    if (!streamData?.event?.slug) return;
    try {
      const response = await axios.get(
        `${API}/livestream/votes/${streamData.event.slug}`,
        getAuthHeaders()
      );
      const newResults = response.data.results || [];
      const newTotal = response.data.total_votes || 0;
      
      // Only update if values changed
      setVoteResults(prev => JSON.stringify(prev) === JSON.stringify(newResults) ? prev : newResults);
      setTotalVotes(prev => prev === newTotal ? prev : newTotal);
    } catch (err) {
      console.error('Error fetching votes:', err);
    }
  };

  const handleVote = async (option) => {
    if (voting || winnerAnnounced) return; // Prevent voting if already voting or winner announced
    
    setVoting(true);
    setSelectedVote(option);
    try {
      await axios.post(
        `${API}/livestream/vote/${streamData.event.slug}`,
        { option },
        getAuthHeaders()
      );
      // Don't fetch vote results immediately since results are hidden until winner announced
      // This prevents the screen from jumping
    } catch (err) {
      console.error('Error voting:', err);
      // Reset selection on error
      setSelectedVote('');
    } finally {
      setVoting(false);
    }
  };

  const fetchScorecard = async () => {
    if (!streamData?.event?.id) return;
    try {
      const response = await axios.get(`${API}/livestream/scorecard/${streamData.event.id}`);
      const newScorecard = response.data.scorecard;
      // Only update state if scorecard actually changed (compare JSON)
      setScorecard(prev => {
        if (JSON.stringify(prev) === JSON.stringify(newScorecard)) {
          return prev; // No change, don't re-render
        }
        return newScorecard;
      });
    } catch (err) {
      console.error('Error fetching scorecard:', err);
    }
  };

  const getVotePercentage = (option) => {
    if (!voteResults || totalVotes === 0) return 0;
    const result = voteResults.find(r => r.option === option);
    return result ? Math.round((result.votes / totalVotes) * 100) : 0;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Compute derived values (before early returns to satisfy React Hooks rules)
  const event = streamData?.event;
  const streamUrl = streamData?.stream_url;
  const customerData = JSON.parse(localStorage.getItem('livestreamCustomer') || '{}');
  const hasUsername = customer?.username || customerData.username;
  const viewerIdentifier = customerData.username || customerData.email?.split('@')[0] || 'viewer';

  // Memoize the stable iframe URL to prevent player reloads
  const stableIframeUrl = useMemo(() => {
    if (playerIframeUrl) return playerIframeUrl;
    if (streamUrl) {
      const defaultCustomerCode = 'xg4xjbetrvqn62vp';
      return `https://customer-${defaultCustomerCode}.cloudflarestream.com/${streamUrl}/iframe`;
    }
    return null;
  }, [playerIframeUrl, streamUrl]);

  // Error states
  if (sessionError) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
        <div className="bg-gradient-to-br from-red-900/40 to-gray-900 rounded-2xl p-8 max-w-md text-center border border-red-500/30">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">Session Ended</h2>
          <p className="text-gray-400 mb-6">
            Someone logged into your account from another device.
            <br /><br />
            <strong className="text-white">Only one device can watch at a time.</strong>
          </p>
          <Button 
            className="w-full bg-red-600 hover:bg-red-500"
            onClick={() => navigate('/live/login')}
          >
            Sign In Again
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-red-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading stream...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
        <div className="bg-white/5 rounded-2xl p-8 max-w-md text-center border border-white/10">
          <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button 
            className="w-full bg-red-600 hover:bg-red-500"
            onClick={() => navigate('/live')}
          >
            Browse Events
          </Button>
        </div>
      </div>
    );
  }

  // Voting Panel Component
  const VotingPanel = ({ onClose, className = '' }) => (
    <div className={`bg-[#0a0a0a]/90 backdrop-blur-xl rounded-2xl p-4 border border-white/10 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-purple-400" />
          <span className="font-bold text-white">{winnerAnnounced ? 'Results' : 'Cast Your Vote'}</span>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
      
      {resultsVisible && <p className="text-xs text-gray-400 mb-3">{totalVotes} votes cast</p>}
      
      {/* Winner Banner */}
      {winnerAnnounced && winner && (
        <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-2 mb-3 text-center">
          <Crown className="h-4 w-4 text-yellow-400 inline mr-1" />
          <span className="text-yellow-400 font-bold text-sm">{winner} wins!</span>
        </div>
      )}
      
      <div className="space-y-2">
        {streamData.voting_options.map((option, index) => {
          const percentage = resultsVisible ? getVotePercentage(option) : 0;
          const isWinner = winner === option;
          const isSelected = selectedVote === option;
          
          return (
            <button
              key={option}
              onClick={() => !voting && !winnerAnnounced && handleVote(option)}
              disabled={voting || winnerAnnounced}
              className={`w-full relative overflow-hidden rounded-xl transition-all ${
                isSelected ? 'ring-2 ring-purple-500' : ''
              } ${isWinner ? 'ring-2 ring-yellow-400' : ''}`}
            >
              {resultsVisible && (
                <div 
                  className={`absolute inset-0 ${
                    index === 0 ? 'bg-red-500/50' : 'bg-blue-500/50'
                  }`}
                  style={{ width: `${percentage}%` }}
                />
              )}
              <div className={`relative p-3 flex items-center justify-between ${
                index === 0 ? 'bg-red-500/20' : 'bg-blue-500/20'
              }`}>
                <div className="flex items-center gap-2">
                  {isWinner && <Crown className="h-4 w-4 text-yellow-400" />}
                  <span className="font-medium text-sm text-white">{option}</span>
                </div>
                {resultsVisible ? (
                  <span className="font-bold text-white">{percentage}%</span>
                ) : (
                  <span className="text-xs text-gray-400">{isSelected ? '✓' : ''}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
      
      {!winnerAnnounced && !resultsVisible && (
        <p className="text-xs text-gray-500 mt-3 text-center">Results revealed after voting ends</p>
      )}
    </div>
  );

  // Voting Modal Component
  const VotingModal = () => (
    <Dialog open={showVotingModal} onOpenChange={setShowVotingModal}>
      <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-lg p-0 overflow-hidden [&>button]:h-10 [&>button]:w-10 [&>button]:rounded-full [&>button]:bg-white/10 [&>button]:hover:bg-white/20 [&>button]:opacity-100 [&>button>svg]:h-6 [&>button>svg]:w-6">
        <div className="p-6">
          <DialogHeader className="pb-4 pr-10">
            <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                winnerAnnounced 
                  ? 'bg-gradient-to-br from-yellow-500 to-orange-500' 
                  : 'bg-gradient-to-br from-purple-500 to-pink-500'
              }`}>
                {winnerAnnounced ? <Crown className="h-5 w-5 text-white" /> : <Trophy className="h-5 w-5 text-white" />}
              </div>
              {winnerAnnounced ? 'Results' : 'Cast Your Vote'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {winnerAnnounced 
                ? 'The winner has been announced!' 
                : 'Vote for your favorite! Results will be revealed when voting closes.'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Winner Banner */}
          {winnerAnnounced && winner && (
            <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl p-4 mb-4 text-center">
              <p className="text-sm text-yellow-200/70">Winner</p>
              <p className="text-2xl font-black text-yellow-400">{winner}</p>
            </div>
          )}
          
          <div className="space-y-4 mt-4">
            {/* Only show vote count if results are visible */}
            {resultsVisible && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Total votes cast</span>
                <span className="font-bold text-purple-400">{totalVotes}</span>
              </div>
            )}
            
            <div className="space-y-3">
              {streamData?.voting_options?.map((option, index) => {
                const percentage = resultsVisible ? getVotePercentage(option) : 0;
                const isWinner = winner === option;
                const isSelected = selectedVote === option;
                
                return (
                  <button
                    key={option}
                    onClick={() => !voting && !winnerAnnounced && handleVote(option)}
                    disabled={voting || winnerAnnounced}
                    className={`w-full relative overflow-hidden rounded-xl transition-all transform ${
                      !winnerAnnounced ? 'hover:scale-[1.02]' : ''
                    } ${
                      isSelected ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-[#1a1a1a]' : ''
                    } ${
                      isWinner ? 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-[#1a1a1a]' : ''
                    }`}
                  >
                    {/* Progress bar - only show if results are visible */}
                    {resultsVisible && (
                      <div 
                        className={`absolute inset-0 transition-all duration-500 ${
                          index === 0 ? 'bg-gradient-to-r from-red-600/60 to-red-500/40' : 'bg-gradient-to-r from-blue-600/60 to-blue-500/40'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    )}
                    
                    {/* Content */}
                    <div className={`relative p-4 flex items-center justify-between ${
                      index === 0 ? 'bg-red-500/20' : 'bg-blue-500/20'
                    }`}>
                      <div className="flex items-center gap-3">
                        {isWinner && (
                          <div className="h-8 w-8 rounded-full bg-yellow-500/30 flex items-center justify-center">
                            <Crown className="h-4 w-4 text-yellow-400" />
                          </div>
                        )}
                        <div className="text-left">
                          <span className="font-bold text-lg text-white">{option}</span>
                          {isSelected && (
                            <p className="text-xs text-purple-300">Your vote</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {resultsVisible ? (
                          <span className="text-2xl font-bold text-white">{percentage}%</span>
                        ) : (
                          <span className="text-sm text-gray-400">
                            {isSelected ? '✓ Voted' : winnerAnnounced ? '' : 'Tap to vote'}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            
            {selectedVote && !winnerAnnounced && (
              <p className="text-center text-sm text-gray-400 mt-4">
                ✓ You voted for <span className="text-purple-400 font-medium">{selectedVote}</span>
              </p>
            )}
            
            {!winnerAnnounced && !resultsVisible && (
              <p className="text-center text-xs text-gray-500 mt-4">
                Results will be revealed when the admin announces the winner
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Vote Button Component (to trigger modal)
  const VoteButton = ({ className = '' }) => (
    <button
      onClick={() => setShowVotingModal(true)}
      className={`group flex items-center gap-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white px-6 py-3 rounded-xl font-bold transition-all transform hover:scale-105 shadow-lg shadow-purple-500/25 ${className}`}
      data-testid="cast-vote-btn"
    >
      <Trophy className="h-5 w-5 group-hover:animate-bounce" />
      <span>{winnerAnnounced ? 'View Results' : 'Cast Your Vote'}</span>
      {totalVotes > 0 && resultsVisible && (
        <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm">{totalVotes}</span>
      )}
    </button>
  );

  // Winner Celebration Overlay
  const WinnerOverlay = () => (
    <Dialog open={showWinnerOverlay} onOpenChange={setShowWinnerOverlay}>
      <DialogContent className="bg-gradient-to-br from-yellow-900/95 to-orange-900/95 border-yellow-500/30 text-white max-w-md text-center overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-4 -left-4 w-24 h-24 bg-yellow-400/20 rounded-full blur-2xl animate-pulse" />
          <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-orange-400/20 rounded-full blur-2xl animate-pulse" />
        </div>
        
        <div className="relative py-8">
          <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center mb-6 animate-bounce">
            <Crown className="h-10 w-10 text-white" />
          </div>
          
          <h2 className="text-3xl font-black text-white mb-2">🎉 WINNER! 🎉</h2>
          <p className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300 mb-4">
            {winner}
          </p>
          
          {resultsVisible && (
            <div className="mt-6 space-y-2">
              {voteResults?.map((r, idx) => (
                <div key={r.option} className={`flex items-center justify-between p-3 rounded-lg ${
                  r.option === winner ? 'bg-yellow-500/30 border border-yellow-500/50' : 'bg-white/10'
                }`}>
                  <span className="font-bold text-white">{r.option}</span>
                  <span className="text-white">{r.votes} votes ({totalVotes > 0 ? Math.round(r.votes / totalVotes * 100) : 0}%)</span>
                </div>
              ))}
              <p className="text-sm text-yellow-200/70 mt-4">{totalVotes} total votes</p>
            </div>
          )}
          
          <Button 
            onClick={() => setShowWinnerOverlay(false)}
            className="mt-6 bg-white text-black hover:bg-gray-100 font-bold"
          >
            Continue Watching
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // ============ DESKTOP LAYOUT (YouTube Style) ============
  if (!isMobile) {
    return (
      <div className="h-screen bg-[#0f0f0f] text-white flex flex-col">
        {/* Username Modal - Inline to prevent re-render issues */}
        <Dialog open={showUsernameModal} onOpenChange={setShowUsernameModal}>
          <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white">Set Your Username</DialogTitle>
              <DialogDescription className="text-gray-400">
                Choose a unique username to chat with others. This cannot be changed later.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Input
                  value={newUsername}
                  onChange={(e) => {
                    setNewUsername(e.target.value);
                    setUsernameError('');
                  }}
                  placeholder="Enter username (3-20 characters)"
                  className="bg-white/10 border-white/20 text-white placeholder-white/50 focus:border-purple-500"
                  maxLength={20}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      saveUsername();
                    }
                  }}
                  data-testid="username-input"
                  autoFocus
                />
                {usernameError && (
                  <p className="text-red-400 text-sm">{usernameError}</p>
                )}
                <p className="text-gray-500 text-xs">
                  Only letters and numbers allowed. Example: JohnDoe123
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                  onClick={() => setShowUsernameModal(false)}
                  disabled={savingUsername}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400"
                  onClick={saveUsername}
                  disabled={savingUsername || !newUsername.trim()}
                  data-testid="save-username-btn"
                >
                  {savingUsername ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    'Save Username'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {/* Desktop Header */}
        <header className="bg-[#0f0f0f] border-b border-white/10 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              className="text-white/70 hover:text-white hover:bg-white/10"
              onClick={() => event?.restricted_navigation ? navigate('/live/my-tickets') : navigate('/live/my-tickets')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              {event?.custom_logo_url ? (
                <img 
                  src={event.custom_logo_url} 
                  alt={event.title || 'Event'} 
                  className="h-10 max-w-[150px] object-contain"
                />
              ) : (
                <>
                  <img 
                    src="/ltv-live-logo.png" 
                    alt="LTV Live" 
                    className="w-10 h-10 object-contain brightness-0 invert"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold">LTV Live</span>
                      {event?.status === 'live' && (
                        <Badge className="bg-red-500 text-white text-xs px-2 animate-pulse">
                          LIVE
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-400">{event?.title}</p>
                  </div>
                </>
              )}
              {event?.custom_logo_url && event?.status === 'live' && (
                <Badge className="bg-red-500 text-white text-xs px-2 animate-pulse ml-2">
                  LIVE
                </Badge>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="border-white/20 text-gray-400">
              <Users className="h-3 w-3 mr-1" />
              {messages.length} comments
            </Badge>
          </div>
        </header>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Video Section */}
          <div className="flex-1 flex flex-col">
            {/* Video Player */}
            <div className="relative bg-[#0a0a0a] aspect-video max-h-[calc(100vh-200px)]">
              <MemoizedVideoPlayer 
                iframeUrl={stableIframeUrl}
                streamUrl={streamUrl}
                viewerIdentifier={viewerIdentifier}
                scorecard={scorecard}
                event={event}
              />
            </div>

            {/* Event Info & Voting */}
            <div className="p-4 flex-1 overflow-y-auto">
              <h1 className="text-xl font-bold mb-2">{event?.title}</h1>
              <p className="text-gray-400 text-sm mb-4">{event?.description}</p>
            </div>
          </div>

          {/* Voting Button - Fixed position on right side above chat */}
          {streamData?.voting_enabled && streamData?.voting_options?.length > 0 && (
            <div className={`fixed bottom-8 z-40 ${showChat ? 'right-[420px]' : 'right-8'}`}>
              <VoteButton />
            </div>
          )}

          {/* Voting Modal */}
          <VotingModal />

          {/* Winner Overlay */}
          <WinnerOverlay />

          {/* Chat Sidebar (YouTube Style) */}
          {showChat && (
            <div className="w-[400px] border-l border-white/10 flex flex-col bg-[#0f0f0f]">
              {/* Chat Header */}
              <div className="p-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-purple-400" />
                  <span className="font-bold">Live Chat</span>
                  {isModerator && (
                    <Badge variant="outline" className="border-yellow-500/50 text-yellow-400 text-xs">MOD</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Notification Bell */}
                  <button 
                    onClick={() => {
                      setShowNotifications(!showNotifications);
                      if (!showNotifications && unreadCount > 0) {
                        markNotificationsRead();
                      }
                    }}
                    className="relative text-gray-400 hover:text-white p-1"
                  >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full text-xs flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  <button 
                    onClick={() => setShowChat(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Notifications Panel */}
              {showNotifications && (
                <div className="p-3 bg-yellow-500/10 border-b border-yellow-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <AtSign className="h-4 w-4 text-yellow-400" />
                    <span className="text-yellow-400 font-bold text-sm">Mentions</span>
                  </div>
                  {notifications.length === 0 ? (
                    <p className="text-gray-400 text-sm">No mentions yet</p>
                  ) : (
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {notifications.slice(0, 5).map(notif => (
                        <div key={notif.id} className="text-sm">
                          <span className="text-purple-400 font-bold">@{notif.from_username}</span>
                          <span className="text-gray-400"> mentioned you: </span>
                          <span className="text-white">{notif.message_preview.slice(0, 50)}...</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Chat Messages */}
              <div 
                ref={chatContainerRef}
                onScroll={handleChatScroll}
                className="flex-1 overflow-y-auto p-4 space-y-3"
              >
                {messages.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageCircle className="h-12 w-12 mx-auto mb-3 text-white/20" />
                    <p className="text-gray-500">No messages yet</p>
                    <p className="text-gray-600 text-sm">Be the first to chat!</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <ChatMessage 
                      key={msg.id} 
                      msg={msg} 
                      isMobile={false} 
                      formatTime={formatTime}
                      currentUsername={customer?.username}
                      isModerator={isModerator}
                      onDelete={deleteMessage}
                    />
                  ))
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Emoji Picker */}
              {showEmojiPicker && (
                <div className="p-3 border-t border-white/10 bg-white/5">
                  <div className="flex flex-wrap gap-2 justify-center">
                    {QUICK_EMOJIS.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => addEmoji(emoji)}
                        className="text-2xl hover:scale-125 transition-transform p-1"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Chat Input */}
              <div className="p-4 border-t border-white/10 flex-shrink-0">
                <form onSubmit={sendMessage} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${
                      showEmojiPicker ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    <Smile className="h-5 w-5" />
                  </button>

                  <div className="flex-1 relative">
                    <input
                      ref={desktopInputRef}
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder={hasUsername ? "Send a message..." : "Set username to chat"}
                      disabled={!hasUsername || sendingMessage}
                      className="w-full h-10 bg-white/10 border border-white/20 rounded-full px-4 pr-12 text-white placeholder-white/50 focus:outline-none focus:border-purple-500 disabled:opacity-50"
                      maxLength={500}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                    />
                    
                    <button
                      type="submit"
                      disabled={!hasUsername || !newMessage.trim() || sendingMessage}
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      {sendingMessage ? (
                        <Loader2 className="h-4 w-4 animate-spin text-white" />
                      ) : (
                        <Send className="h-4 w-4 text-white" />
                      )}
                    </button>
                  </div>
                </form>
                
                {!hasUsername && (
                  <p className="text-center text-xs text-gray-500 mt-2">
                    <button 
                      onClick={() => setShowUsernameModal(true)} 
                      className="text-purple-400 hover:underline"
                      data-testid="set-username-desktop-btn"
                    >
                      Set a username
                    </button> to chat
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Show Chat Button (when hidden) */}
        {!showChat && (
          <button
            onClick={() => setShowChat(true)}
            className="fixed bottom-6 right-6 h-14 w-14 rounded-full bg-purple-500 text-white shadow-lg flex items-center justify-center hover:bg-purple-400 transition-colors"
          >
            <MessageCircle className="h-6 w-6" />
          </button>
        )}

        {/* CSS */}
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out;
          }
          @keyframes floatWatermark {
            0% { top: 20%; left: 10%; }
            25% { top: 60%; left: 70%; }
            50% { top: 30%; left: 50%; }
            75% { top: 70%; left: 20%; }
            100% { top: 20%; left: 10%; }
          }
          .watermark-float {
            animation: floatWatermark 30s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }

  // ============ MOBILE LAYOUT (Instagram Style) ============

  // ============ MOBILE LAYOUT (TikTok Style - Full Screen) ============
  return (
    <div id="mobile-stream-container" className="fixed inset-0 bg-black text-white overflow-hidden">
      {/* Username Modal */}
      <Dialog open={showUsernameModal} onOpenChange={setShowUsernameModal}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-[90vw] rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-white">Set Your Username</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">
              Choose a username to chat with others.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            <Input
              value={newUsername}
              onChange={(e) => {
                setNewUsername(e.target.value);
                setUsernameError('');
              }}
              placeholder="Enter username"
              className="bg-white/10 border-white/20 text-white placeholder-white/50 focus:border-purple-500"
              maxLength={20}
              data-testid="username-input"
              autoFocus
            />
            {usernameError && (
              <p className="text-red-400 text-sm">{usernameError}</p>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10"
                onClick={() => setShowUsernameModal(false)}
                disabled={savingUsername}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500"
                onClick={saveUsername}
                disabled={savingUsername || !newUsername.trim()}
                data-testid="save-username-btn"
              >
                {savingUsername ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Full Screen Video Container */}
      <div className="absolute inset-0">
        <MobileVideoPlayer 
          streamUrl={streamUrl}
          viewerIdentifier={viewerIdentifier}
          scorecard={scorecard}
          event={event}
          videoRef={mobileVideoRef}
          containerRef={videoContainerRef}
          isMuted={isMuted}
          viewerCount={viewerCount}
        />
      </div>

      {/* Voting Modal */}
      <VotingModal />
      
      {/* Winner Overlay */}
      <WinnerOverlay />

      {/* Top Left - Back Button Only (minimal, doesn't block video) */}
      <div className="absolute top-4 left-4 z-30" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <Button 
          variant="ghost" 
          size="icon"
          className="text-white bg-[#0a0a0a]/40 hover:bg-[#0a0a0a]/60 backdrop-blur-sm rounded-full h-10 w-10"
          onClick={() => navigate('/live/my-tickets')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
      </div>

      {/* Right Side Actions (TikTok style vertical stack) - z-50 to be above chat overlay */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 z-50 flex flex-col items-center gap-5">
        {/* Vote Button */}
        {streamData?.voting_enabled && streamData?.voting_options?.length > 0 && (
          <button
            onClick={() => setShowVotingModal(true)}
            className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg"
          >
            <Trophy className="h-6 w-6 text-white" />
          </button>
        )}
        
        {/* Chat Toggle */}
        <button
          onClick={() => setShowChat(!showChat)}
          className={`h-12 w-12 rounded-full flex items-center justify-center backdrop-blur-sm shadow-lg transition-all relative ${
            showChat ? 'bg-purple-500 text-white' : 'bg-[#0a0a0a]/50 text-white'
          }`}
        >
          <MessageCircle className="h-6 w-6" />
          {!showChat && messages.length > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
              {messages.length > 99 ? '99+' : messages.length}
            </span>
          )}
        </button>
        
        {/* Fullscreen Button */}
        <button
          onClick={() => {
            const video = mobileVideoRef.current;
            const container = videoContainerRef.current;
            
            if (!video && !container) {
              console.log('No video or container ref available');
              return;
            }
            
            // Check if in fullscreen
            const isInFullscreen = document.fullscreenElement || document.webkitFullscreenElement || document.webkitIsFullScreen;
            
            if (isInFullscreen) {
              // Exit fullscreen
              if (document.exitFullscreen) document.exitFullscreen();
              else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
              else if (document.webkitCancelFullScreen) document.webkitCancelFullScreen();
            } else {
              // Enter fullscreen with watermark
              // On iOS Safari, we must use video.webkitEnterFullscreen (no watermark possible)
              // On other browsers, we use the container for fullscreen WITH watermark
              try {
                // First, try container fullscreen (keeps watermark visible)
                if (container?.requestFullscreen) {
                  container.requestFullscreen();
                } else if (container?.webkitRequestFullscreen) {
                  container.webkitRequestFullscreen();
                } else if (video?.webkitEnterFullscreen) {
                  // iOS Safari fallback - video only (no watermark in native iOS fullscreen)
                  video.play().then(() => {
                    video.webkitEnterFullscreen();
                  }).catch(() => {
                    video.webkitEnterFullscreen();
                  });
                } else if (video?.requestFullscreen) {
                  video.requestFullscreen();
                }
              } catch (err) {
                console.log('Fullscreen error:', err);
              }
            }
          }}
          className="h-12 w-12 rounded-full bg-black/50 flex items-center justify-center backdrop-blur-sm shadow-lg"
          data-testid="fullscreen-btn"
        >
          {isFullscreen ? <Minimize className="h-6 w-6 text-white" /> : <Maximize className="h-6 w-6 text-white" />}
        </button>
        
        {/* Volume/Unmute Button */}
        <button
          onClick={() => {
            const video = mobileVideoRef.current;
            if (video) {
              const newMuted = !video.muted;
              video.muted = newMuted;
              setIsMuted(newMuted);
              // Also try to play if unmuting
              if (!newMuted) {
                video.play().catch(() => {});
              }
            } else {
              // Toggle state even without video ref (UI feedback)
              setIsMuted(!isMuted);
            }
          }}
          className={`h-12 w-12 rounded-full flex items-center justify-center backdrop-blur-sm shadow-lg ${
            isMuted ? 'bg-red-500' : 'bg-green-500'
          }`}
          data-testid="volume-btn"
        >
          {isMuted ? <VolumeX className="h-6 w-6 text-white" /> : <Volume2 className="h-6 w-6 text-white" />}
        </button>
        
        {/* Heart/Like Button - Sends instantly */}
        <button
          onClick={() => {
            if (hasUsername) {
              sendInstantMessage('❤️');
            } else {
              setShowUsernameModal(true);
            }
          }}
          className="h-12 w-12 rounded-full bg-[#0a0a0a]/50 flex items-center justify-center backdrop-blur-sm shadow-lg"
          data-testid="heart-btn"
        >
          <Heart className="h-6 w-6 text-red-500" fill="currentColor" />
        </button>
        
        {/* Picture-in-Picture Button - Shows on all browsers that support it */}
        <button
          onClick={togglePiP}
          className={`h-12 w-12 rounded-full flex items-center justify-center backdrop-blur-sm shadow-lg ${
            isPiPActive ? 'bg-blue-500' : 'bg-[#0a0a0a]/50'
          }`}
          data-testid="pip-btn"
        >
          {/* PiP icon - use SVG */}
          <svg className="h-6 w-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <rect x="12" y="10" width="8" height="6" rx="1" fill="currentColor" />
          </svg>
        </button>
      </div>

      {/* Bottom Chat Overlay (TikTok Style - Fixed position for keyboard) */}
      {showChat && (
        <div className="fixed bottom-0 left-0 right-0 z-40" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-gradient-to-t from-black via-black/80 to-transparent pt-8 pb-2 px-2">
            {/* Chat Messages - Compact TikTok style */}
            <div 
              ref={chatContainerRef}
              onScroll={handleChatScroll}
              className="max-h-[20vh] overflow-y-auto mb-2 space-y-1"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {messages.length === 0 ? (
                <p className="text-white/40 text-sm">Be the first to chat!</p>
              ) : (
                messages.slice(-20).map((msg) => (
                  <div key={msg.id} className="flex items-start gap-2 animate-fadeIn">
                    <span className={`text-sm font-semibold flex-shrink-0 ${
                      msg.is_admin ? 'text-yellow-400' : 'text-purple-400'
                    }`}>
                      {msg.username}
                    </span>
                    <span className="text-sm text-white/90 break-words">
                      {msg.message}
                    </span>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input - Full width on mobile */}
            <form onSubmit={sendMessage} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className={`h-9 w-9 rounded-full flex-shrink-0 flex items-center justify-center ${
                  showEmojiPicker ? 'bg-purple-500 text-white' : 'bg-[#0a0a0a]/50 text-white/70'
                }`}
              >
                <Smile className="h-5 w-5" />
              </button>

              <div className="flex-1 relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={hasUsername ? "Say something..." : "Set username to chat"}
                  disabled={!hasUsername || sendingMessage}
                  className="w-full h-10 bg-[#0a0a0a]/50 backdrop-blur-sm border border-white/30 rounded-full px-4 pr-12 text-white placeholder-white/50 focus:outline-none focus:border-purple-500 disabled:opacity-50 text-base"
                  maxLength={500}
                  enterKeyHint="send"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                      // Blur input to dismiss keyboard after sending
                      e.target.blur();
                    }
                  }}
                />
                
                <button
                  type="submit"
                  disabled={!hasUsername || !newMessage.trim() || sendingMessage}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center disabled:opacity-30"
                >
                  {sendingMessage ? (
                    <Loader2 className="h-3 w-3 animate-spin text-white" />
                  ) : (
                    <Send className="h-3 w-3 text-white" />
                  )}
                </button>
              </div>
            </form>

            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div className="mt-2 bg-[#0a0a0a]/60 backdrop-blur-sm rounded-xl p-2">
                <div className="flex flex-wrap gap-1 justify-center">
                  {QUICK_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => addEmoji(emoji)}
                      className="text-xl p-1"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Username Prompt */}
            {!hasUsername && (
              <button 
                onClick={() => setShowUsernameModal(true)}
                className="mt-2 text-purple-400 text-xs underline"
                data-testid="set-username-mobile-btn"
              >
                Set a username to chat
              </button>
            )}
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        @keyframes floatWatermark {
          0% { top: 20%; left: 10%; }
          25% { top: 60%; left: 70%; }
          50% { top: 30%; left: 50%; }
          75% { top: 70%; left: 20%; }
          100% { top: 20%; left: 10%; }
        }
        .watermark-float {
          animation: floatWatermark 30s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default LiveWatchStream;
